import React from 'react';
import Config from 'config';
import { sortBy } from 'lodash';
import { entries, flow, filter, groupBy, reduce } from 'lodash/fp';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import useProductCache from 'store/cache/product-cache';
import { OrganizationLicenseUsage } from 'types/schema-generated-types';
import ProductFamilyLicenseUsageCard from './ProductFamilyUsageCard';

interface ProductFamilyUsageTileViewProps {
  licenseUsage: OrganizationLicenseUsage[];
  rootOrganizationLicenseUsage?: OrganizationLicenseUsage[];
}

interface ProductFamilyUsage {
  code: string;
  name: string;
  usage: OrganizationLicenseUsage[];
  rootUsage?: OrganizationLicenseUsage[];
}

const { combinedProductFamilyTiles } = Config;

const ProductFamilyUsageTileView = ({
  licenseUsage,
  rootOrganizationLicenseUsage
}: ProductFamilyUsageTileViewProps) => {
  const classes = useStyles();
  const [{ findProductFamilyByCode }] = useProductCache();

  // These will be regular product families which are *not* in the combinedProductFamilyTiles list.
  const productFamilyUsages = flow(
    // Filter usages which belong to combined tiles.
    filter<OrganizationLicenseUsage>(usage => {
      const productFamilyCode = usage.product.family.code;
      return !combinedProductFamilyTiles.some(x =>
        x.productFamilyCodes.includes(productFamilyCode)
      );
    }),
    groupBy('product.family.code'),
    entries,
    reduce((acc: ProductFamilyUsage[], [code, usage]: [string, OrganizationLicenseUsage[]]) => {
      const productFamily = findProductFamilyByCode(code);
      if (!productFamily) return acc;

      const rootUsage = rootOrganizationLicenseUsage?.filter(x => x.product.family.code === code);

      return [...acc, { code: productFamily.code, name: productFamily.name, usage, rootUsage }];
    }, [])
  )(licenseUsage) as ProductFamilyUsage[];

  const combinedProductFamilyUsages = combinedProductFamilyTiles.reduce(
    (acc, { productFamilyCodes, name }) => {
      const usage = licenseUsage.filter(x => productFamilyCodes.includes(x.product.family.code));
      if (usage.length === 0) return acc;

      const rootUsage = rootOrganizationLicenseUsage?.filter(x =>
        productFamilyCodes.includes(x.product.family.code)
      );

      return [...acc, { code: productFamilyCodes.join(), name, usage, rootUsage }];
    },
    [] as ProductFamilyUsage[]
  );

  const allProductFamilyUsages = sortBy(
    [...productFamilyUsages, ...combinedProductFamilyUsages],
    'name'
  );

  return (
    <Grid container spacing={2}>
      {allProductFamilyUsages.map(({ code, name, usage, rootUsage }) => (
        <Grid item key={`product-family-${code}-license-usage`} xs={12}>
          <Typography variant="h2" className={classes.header}>
            {name}
          </Typography>
          <ProductFamilyLicenseUsageCard
            licenseUsage={usage}
            rootOrganizationLicenseUsage={rootUsage}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4)
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

export default ProductFamilyUsageTileView;
